import { IAuditedEntity, Quantity } from '@elderbyte/ngx-starter'
import { JsonMap } from '../work-orders/work-order'
import { ErrorData, ValidationState, WorkTransferState } from '../work-items/work-item'
import { PropertySetValue } from '../work-items/bom/bom'

export enum WorkBatchState {
  /**
   * The batch is just a preview and not really existent.
   */
  PREVIEW = 'PREVIEW',

  /**
   * The batch is real and currently preparing.
   * WorkItems are assigned to this batch.
   */
  PREPARING = 'PREPARING',

  /**
   * The batch is prepared and ready for processing.
   */
  PREPARED = 'PREPARED',

  /**
   * The batch is running / in production
   */
  PROCESSING = 'PROCESSING',

  /**
   * The batch has been completed
   */
  COMPLETED = 'COMPLETED',
}

export enum WorkBatchSegmentState {
  /**
   * The segment processing has not yet started
   */
  OPEN = 'OPEN',

  /**
   * The segment is processing
   */
  PROCESSING = 'PROCESSING',

  /**
   * The execution is completed.
   * All work-items in this segment are completed.
   */
  COMPLETED = 'COMPLETED',

  UNKNOWN = 'UNKNOWN',
}

export class NewWorkBatch {
  constructor(
    public workOrderId: string,
    public productionLinePath: string,
    public workStationId: string
  ) {}
}

interface MismatchReason {
  inputValue: PropertySetValue
  operator: string
  values: Set<string>
}

export class WorkBatch {
  public id: string

  public number: string

  /**
   * The facility
   */
  public facility: string

  /**
   * The id of the owning prod order
   */
  public prodOrderId: string

  /**
   * The id of the owning work order
   */
  public workOrderId: string

  public state: WorkBatchState

  public validationState: ValidationState
  public invalidityReason?: string

  public processPlans: string[] = []

  public productionLine: WorkBatchLine // Line

  public numberOfItems: number

  public weight: Quantity

  // effective weight after prepare phase?

  public quantities: JsonMap<Quantity>

  public mismatchReason?: MismatchReason

  public audit: IAuditedEntity
}

export class WorkBatchLine {
  public productionLinePath: string
  public segments: WorkBatchSegment[]
  public packaging: BatchPackaging

  public static isLastSegment(line: WorkBatchLine, workCenterId: string): boolean {
    const segment = WorkBatchLine.getSegment(line, workCenterId)
    const lastSegment = WorkBatchLine.getLastSegment(line)
    return segment.workCenterId === lastSegment.workCenterId
  }

  public static getLastSegment(line: WorkBatchLine) {
    return line.segments[line.segments.length - 1]
  }

  public static getSegment(line: WorkBatchLine, workCenterId: string): WorkBatchSegment {
    return line.segments.find((segment) => segment.workCenterId === workCenterId)
  }

  public static getPosInSequence(line: WorkBatchLine, workCenterId: string): number {
    return line.segments.indexOf(this.getSegment(line, workCenterId))
  }
}

export interface WorkBatchSegment {
  workCenterId: string
  workCenterNumber: string
  workCenterName: string
  state: WorkBatchSegmentState
  itemLabelsReport?: WorkBatchReporting
  transferState: WorkTransferState
  transferError?: ErrorData
  transferLock?: Date
  packaging?: WorkBatchSegmentPackaging
}

interface BatchPackaging {
  packageCalculationEnabled: boolean
  packageStagingAreaPath?: string
  packageSharingEnabled: boolean
}

export interface WorkBatchSegmentPackaging {
  mode: WorkBatchSegmentPackagingMode
}

export enum WorkBatchSegmentPackagingMode {
  DISABLED = 'DISABLED',

  OPTIONAL = 'OPTIONAL',

  FINAL = 'FINAL',
}

export interface WorkBatchReporting {
  jobId?: string

  jobState: ReportState
  jobError?: ErrorData

  reportUrn?: string
}

export enum ReportState {
  /**
   * Provision has not been requested.
   */
  OPEN = 'OPEN',

  /**
   * Report is being generated.
   */
  PROCESSING = 'PROCESSING',

  /**
   * Report has been generated.
   */
  COMPLETED = 'COMPLETED',

  /**
   * Report generation has failed.
   */
  FAILED = 'FAILED',
}

export class WorkBatchCommand {
  constructor(
    public action: WorkBatchAction,
    public workBatchId: string,
    public itemIds: string[] = []
  ) {}
}

export enum WorkBatchAction {
  /**
   * Abort and delete the batch if it has not been started yet, delete corresponding preview packages.
   */
  ABORT_BATCH = 'ABORT_BATCH',

  /**
   * Abort and delete the batch regardless of current state, delete corresponding preview packages.
   */
  FORCE_ABORT_BATCH = 'FORCE_ABORT_BATCH',

  /**
   * Reset segment transfer locks
   */
  RESET_TRANSFER_LOCKS = 'RESET_TRANSFER_LOCKS',
}

export class WorkBatchCommandResult {
  public action: WorkBatchAction
  public success: boolean
  public workBatches: WorkBatch[]
}
